'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { BookMarked, FileText, Building2, MapPin, Trash2, Download, Eye, Filter, Clock, TrendingUp, Loader2, RefreshCw, Info, ChevronRight, Heart, Zap } from 'lucide-react';
import { savedFiltersService, SavedFilter } from '@/lib/services/savedFiltersService';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';
import Link from 'next/link';

export const dynamic = 'force-dynamic';

interface WatchlistItem {
  id: string;
  project_id: string;
  created_at: string;
  projects?: {
    id: string;
    project_name: string;
    developer_name: string;
    micro_market: string;
    price_per_sqft: number;
    inventory_sold_pct: number;
    project_status: string;
    slug: string;
  };
}

interface ReportDownload {
  id: string;
  project_ids: string[];
  project_count: number;
  report_type: string;
  plan: string;
  is_extra: boolean;
  downloaded_at: string;
}

function SkeletonCard() {
  return (
    <div className="card-base p-4 space-y-3 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="h-4 bg-muted rounded w-1/3" />
        <div className="h-6 bg-muted rounded w-16" />
      </div>
      <div className="h-3 bg-muted rounded w-2/3" />
      <div className="h-3 bg-muted rounded w-1/2" />
    </div>
  );
}

const statusBadge: Record<string, string> = {
  'Active Sales': 'bg-amber-500/15 text-amber-400',
  'Nearing Possession': 'bg-emerald-500/15 text-emerald-400',
  'Pre-Launch': 'bg-sky-500/15 text-sky-400',
  'Sold Out': 'bg-violet-500/15 text-violet-400',
  'Delivered': 'bg-muted text-muted-foreground',
};

const reportTypeLabel: Record<string, { label: string; color: string }> = {
  pdf_investor: { label: 'Investor PDF', color: 'text-blue-400' },
  excel_project: { label: 'Project Excel', color: 'text-emerald-400' },
  excel_tower: { label: 'Tower Inventory', color: 'text-amber-400' },
};

export default function SavedReportsPage() {
  const { user } = useAuth();
  const supabase = createClient();

  const [activeTab, setActiveTab] = useState<'watchlist' | 'filters' | 'downloads'>('watchlist');
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [downloads, setDownloads] = useState<ReportDownload[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) setUserId(authUser.id);
    })();
  }, []);

  const loadData = useCallback(async (uid: string) => {
    setLoading(true);
    const [wl, sf, dl] = await Promise.all([
      supabase
        .from('project_watchlist')
        .select('*, projects(id, project_name, developer_name, micro_market, price_per_sqft, inventory_sold_pct, project_status, slug)')
        .eq('user_id', uid)
        .order('created_at', { ascending: false }),
      savedFiltersService.getSavedFilters(uid),
      supabase
        .from('report_downloads')
        .select('id, project_ids, project_count, report_type, plan, is_extra, downloaded_at')
        .eq('user_id', uid)
        .order('downloaded_at', { ascending: false })
        .limit(50),
    ]);
    setWatchlist((wl.data as WatchlistItem[]) ?? []);
    setSavedFilters(sf);
    setDownloads((dl.data as ReportDownload[]) ?? []);
    setLoading(false);
  }, [supabase]);

  useEffect(() => {
    if (userId) loadData(userId);
  }, [userId, loadData]);

  const handleRemoveWatchlist = async (id: string) => {
    setDeletingId(id);
    const { error } = await supabase.from('project_watchlist').delete().eq('id', id);
    setDeletingId(null);
    if (!error) {
      toast.success('Removed from watchlist');
      setWatchlist((prev) => prev.filter((w) => w.id !== id));
    } else {
      toast.error('Failed to remove');
    }
  };

  const handleDeleteFilter = async (id: string) => {
    setDeletingId(id);
    const ok = await savedFiltersService.deleteSavedFilter(id);
    setDeletingId(null);
    if (ok) {
      toast.success('Filter deleted');
      setSavedFilters((prev) => prev.filter((f) => f.id !== id));
    } else {
      toast.error('Failed to delete filter');
    }
  };

  const totalSaved = watchlist.length + savedFilters.length;
  const thisMonthDownloads = downloads.filter((d) => {
    const date = new Date(d.downloaded_at);
    const now = new Date();
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  }).length;

  return (
    <AppLayout activeRoute="/saved">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
              <BookMarked size={22} className="text-blue-400 shrink-0" />
              Saved Reports & Watchlist
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Your personalized investment research hub — tracked projects, saved searches, and report history
            </p>
          </div>
          <button
            onClick={() => userId && loadData(userId)}
            className="self-start sm:self-auto flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/60 hover:bg-muted text-muted-foreground hover:text-foreground text-sm transition-all"
          >
            <RefreshCw size={14} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {[
            { label: 'Watchlisted', value: watchlist.length, icon: Heart, color: 'text-rose-400', bg: 'bg-rose-500/10' },
            { label: 'Saved Filters', value: savedFilters.length, icon: Filter, color: 'text-blue-400', bg: 'bg-blue-500/10' },
            { label: 'Reports Downloaded', value: downloads.length, icon: Download, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
            { label: 'This Month', value: thisMonthDownloads, icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/10' },
          ].map((kpi) => (
            <div key={kpi.label} className="card-base p-3 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg ${kpi.bg} flex items-center justify-center shrink-0`}>
                <kpi.icon size={16} className={kpi.color} />
              </div>
              <div className="min-w-0">
                <p className="text-lg font-bold text-foreground metric-value">{kpi.value}</p>
                <p className="text-[10px] text-muted-foreground truncate">{kpi.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI insight banner */}
      <div className="card-base p-3 mb-5 flex items-start gap-3 border-blue-500/20 bg-blue-500/5">
        <Zap size={16} className="text-blue-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-blue-400">Portfolio Intelligence</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Your watchlisted projects are tracked for price movements, inventory changes, and possession updates. Download investor-grade PDF reports for any project directly from your watchlist.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border mb-5 gap-1 overflow-x-auto scrollbar-thin">
        {[
          { key: 'watchlist', label: 'Watchlist', count: watchlist.length, icon: Heart },
          { key: 'filters', label: 'Saved Searches', count: savedFilters.length, icon: Filter },
          { key: 'downloads', label: 'Download History', count: downloads.length, icon: Download },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as typeof activeTab)}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${
              activeTab === tab.key
                ? 'text-blue-400 border-blue-500' :'text-muted-foreground border-transparent hover:text-foreground'
            }`}
          >
            <tab.icon size={13} />
            {tab.label}
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${activeTab === tab.key ? 'bg-blue-500/20 text-blue-400' : 'bg-muted text-muted-foreground'}`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Watchlist Tab */}
      {activeTab === 'watchlist' && (
        <div className="space-y-3">
          {loading ? (
            [1, 2, 3].map((i) => <SkeletonCard key={i} />)
          ) : watchlist.length === 0 ? (
            <div className="card-base p-8 text-center">
              <Heart size={32} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground">Your watchlist is empty</p>
              <p className="text-xs text-muted-foreground mt-1 mb-4">Bookmark projects from the listings page to track them here</p>
              <Link href="/project-listings-screen" className="btn-primary text-sm py-2 px-4 inline-flex items-center gap-2">
                <Building2 size={14} />
                Browse Projects
              </Link>
            </div>
          ) : (
            watchlist.map((item) => {
              const proj = item.projects;
              if (!proj) return null;
              return (
                <div key={item.id} className="card-base p-4 hover:border-blue-500/30 transition-all">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                        <Building2 size={18} className="text-blue-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <p className="text-sm font-semibold text-foreground">{proj.project_name}</p>
                          <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${statusBadge[proj.project_status] ?? 'bg-muted text-muted-foreground'}`}>
                            {proj.project_status}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                          <MapPin size={10} />{proj.micro_market} · {proj.developer_name}
                        </p>
                        <div className="flex flex-wrap gap-4">
                          <div>
                            <p className="text-[10px] text-muted-foreground">Price/sqft</p>
                            <p className="text-xs font-semibold text-foreground metric-value">₹{proj.price_per_sqft?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-muted-foreground">Absorption</p>
                            <p className="text-xs font-semibold text-foreground metric-value">{proj.inventory_sold_pct?.toFixed(0)}%</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-muted-foreground">Saved on</p>
                            <p className="text-xs text-muted-foreground">{new Date(item.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Link
                        href={`/project-detail-screen?id=${proj.id}`}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-blue-400 hover:bg-blue-500/10 transition-all"
                        title="View project"
                      >
                        <Eye size={14} />
                      </Link>
                      <button
                        onClick={() => handleRemoveWatchlist(item.id)}
                        disabled={deletingId === item.id}
                        title="Remove from watchlist"
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10 transition-all"
                      >
                        {deletingId === item.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Saved Filters Tab */}
      {activeTab === 'filters' && (
        <div className="space-y-3">
          {loading ? (
            [1, 2, 3].map((i) => <SkeletonCard key={i} />)
          ) : savedFilters.length === 0 ? (
            <div className="card-base p-8 text-center">
              <Filter size={32} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground">No saved searches</p>
              <p className="text-xs text-muted-foreground mt-1 mb-4">Save your search filters from the Alerts page to monitor market conditions</p>
              <Link href="/alerts" className="btn-primary text-sm py-2 px-4 inline-flex items-center gap-2">
                <Filter size={14} />
                Go to Alerts
              </Link>
            </div>
          ) : (
            savedFilters.map((filter) => (
              <div key={filter.id} className="card-base p-4 hover:border-blue-500/30 transition-all">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                      <Filter size={18} className="text-emerald-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-foreground">{filter.filter_name}</p>
                        <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${filter.email_alerts_enabled ? 'bg-emerald-500/15 text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
                          {filter.email_alerts_enabled ? 'Alerts On' : 'Alerts Off'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-1.5">
                        {filter.micro_market && (
                          <span className="px-2 py-0.5 bg-muted rounded text-[10px] text-muted-foreground flex items-center gap-1">
                            <MapPin size={9} />{filter.micro_market}
                          </span>
                        )}
                        {filter.project_type && (
                          <span className="px-2 py-0.5 bg-muted rounded text-[10px] text-muted-foreground flex items-center gap-1">
                            <Building2 size={9} />{filter.project_type}
                          </span>
                        )}
                        {(filter.min_price_sqft || filter.max_price_sqft) && (
                          <span className="px-2 py-0.5 bg-muted rounded text-[10px] text-muted-foreground">
                            ₹{filter.min_price_sqft?.toLocaleString() ?? '0'}–{filter.max_price_sqft?.toLocaleString() ?? '∞'}/sqft
                          </span>
                        )}
                        {filter.project_status && (
                          <span className="px-2 py-0.5 bg-muted rounded text-[10px] text-muted-foreground">{filter.project_status}</span>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-2">
                        Saved {new Date(filter.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Link
                      href={`/project-listings-screen?market=${encodeURIComponent(filter.micro_market ?? '')}&type=${encodeURIComponent(filter.project_type ?? '')}`}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-blue-400 hover:bg-blue-500/10 transition-all"
                      title="Apply filter"
                    >
                      <ChevronRight size={14} />
                    </Link>
                    <button
                      onClick={() => handleDeleteFilter(filter.id)}
                      disabled={deletingId === filter.id}
                      title="Delete filter"
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10 transition-all"
                    >
                      {deletingId === filter.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Downloads Tab */}
      {activeTab === 'downloads' && (
        <div className="space-y-3">
          {loading ? (
            [1, 2, 3].map((i) => <SkeletonCard key={i} />)
          ) : downloads.length === 0 ? (
            <div className="card-base p-8 text-center">
              <Download size={32} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground">No downloads yet</p>
              <p className="text-xs text-muted-foreground mt-1 mb-4">Generate and download PDF or Excel reports from the Reports page</p>
              <Link href="/reports" className="btn-primary text-sm py-2 px-4 inline-flex items-center gap-2">
                <FileText size={14} />
                Go to Reports
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-4 py-3 text-muted-foreground font-semibold uppercase tracking-wider">Report Type</th>
                    <th className="text-left px-4 py-3 text-muted-foreground font-semibold uppercase tracking-wider hidden sm:table-cell">Projects</th>
                    <th className="text-left px-4 py-3 text-muted-foreground font-semibold uppercase tracking-wider hidden sm:table-cell">Plan</th>
                    <th className="text-left px-4 py-3 text-muted-foreground font-semibold uppercase tracking-wider hidden md:table-cell">Date</th>
                    <th className="text-left px-4 py-3 text-muted-foreground font-semibold uppercase tracking-wider">Extra</th>
                  </tr>
                </thead>
                <tbody>
                  {downloads.map((dl) => {
                    const typeInfo = reportTypeLabel[dl.report_type] ?? { label: dl.report_type, color: 'text-muted-foreground' };
                    return (
                      <tr key={dl.id} className="border-b border-border hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3">
                          <span className={`font-medium ${typeInfo.color}`}>{typeInfo.label}</span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                          {dl.project_count ?? dl.project_ids?.length ?? 1} project{(dl.project_count ?? 1) !== 1 ? 's' : ''}
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className="capitalize text-muted-foreground">{dl.plan}</span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                          {new Date(dl.downloaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-4 py-3">
                          {dl.is_extra ? (
                            <span className="px-1.5 py-0.5 bg-amber-500/15 text-amber-400 text-[10px] font-semibold rounded">Paid</span>
                          ) : (
                            <span className="px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 text-[10px] font-semibold rounded">Free</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Investment insights section */}
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="card-base p-5">
          <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <TrendingUp size={15} className="text-emerald-400" />
            Investment Research Tips
          </h2>
          <ul className="space-y-2">
            {[
              'Track 5–10 projects across 2–3 micro-markets for diversified insights',
              'Download quarterly reports to monitor price appreciation trends',
              'Set price alerts 5–10% below current market to identify entry opportunities',
              'Compare absorption rates across similar projects to gauge demand',
            ].map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                <span className="w-4 h-4 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center text-[9px] font-bold shrink-0 mt-0.5">{i + 1}</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
        <div className="card-base p-5">
          <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Info size={15} className="text-blue-400" />
            About Saved Reports
          </h2>
          <div className="space-y-2">
            {[
              { q: 'How long are reports stored?', a: 'Download history is retained for 12 months.' },
              { q: 'Can I re-download a report?', a: 'Navigate to the project and regenerate from the Reports page.' },
              { q: 'Are watchlist prices live?', a: 'Prices update daily from our Pune market data feed.' },
            ].map((faq, i) => (
              <div key={i} className="border-b border-border pb-2 last:border-0 last:pb-0">
                <p className="text-xs font-semibold text-foreground mb-0.5">{faq.q}</p>
                <p className="text-xs text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
